import { SnackBarM } from "@common/components/alert/snackbar/SnackBar"
import { apiRequest } from "@data/redux/actions/api"
import {
    resetIntercomIdentification,
    setIntercomReady,
    setIntercomUnreadCount,
    setPendingIntercomOpen,
} from "@data/redux/actions/intercom"
import React, { useCallback, useEffect, useRef, useState } from "react"
import {
    AppState,
    NativeEventEmitter,
    NativeModules,
    Platform,
} from "react-native"
import { connect, useDispatch, useSelector } from "react-redux"
import { familyService } from "../../../data/services/familyService"
import { INTERCOM_SESSION, intercomSession } from "./supportChatAccess"

let Intercom = null
let IntercomEvents = null
let IntercomContent = null
if (Platform.OS !== "web") {
    try {
        const IntercomRN = require("@intercom/intercom-react-native")
        Intercom = IntercomRN.default
        IntercomEvents = IntercomRN.IntercomEvents
        IntercomContent = IntercomRN.IntercomContent
    } catch (e) {
        console.warn("[Intercom] native module unavailable:", e)
    }
}

// Official Intercom JS SDK — web only. Required lazily so the module is never
// evaluated on native (it touches `window` at call time). Exposes default()
// (loads the snippet + boots) plus boot/shutdown/update/show proxies.
let IntercomWebSdk = null
if (Platform.OS === "web") {
    try {
        IntercomWebSdk = require("@intercom/messenger-js-sdk")
    } catch (e) {
        console.warn("[Intercom] web SDK unavailable:", e)
    }
}

// The SDK's default initialiser loads the widget once per page lifecycle; after
// that its re-init path only issues an `update`, which can't switch Intercom
// users. So we call it once, then drive every re-identify with shutdown+boot.
let webIntercomLoaded = false

// Manual JS-side initialize per Intercom RN docs (useManualInit: true in the
// Expo plugin config). The native module is initialized exactly once per app
// lifecycle; subsequent calls reuse the same Promise so concurrent screens
// don't race.
let intercomInitPromise = null
const initializeIntercomNative = (appId, apiKey) => {
    if (!Intercom || Platform.OS === "web") return Promise.resolve(false)
    if (intercomInitPromise) return intercomInitPromise
    if (!apiKey || !appId) {
        console.warn(
            "[Intercom] Missing API key or appId — skipping native init.",
            { hasApiKey: !!apiKey, hasAppId: !!appId, platform: Platform.OS }
        )
        return Promise.resolve(false)
    }
    intercomInitPromise = Intercom.initialize(apiKey, appId)
        .then(() => true)
        .catch((e) => {
            intercomInitPromise = null
            throw e
        })
    return intercomInitPromise
}

// Server signs the identity JWT with a 24h exp. We proactively treat anything
// older than this as stale and refetch it (device clock, no fragile decode), so
// the messenger never presents a token Intercom would reject as expired.
export const JWT_MAX_AGE_MS = 12 * 60 * 60 * 1000
export const isIntercomJwtFresh = (fetchedAt) =>
    !!fetchedAt && Date.now() - fetchedAt < JWT_MAX_AGE_MS

// Mirrors the old HelpFAB positioning: 65px above the tab bar, and on
// large web screens shifted inward to align with the 2/3-width content area.
const computeWebLauncherPadding = () => {
    if (typeof window === "undefined") return { vertical: 65, horizontal: 15 }
    const width = window.innerWidth || 0
    const isLargeScreen = width > 800
    const horizontal = isLargeScreen ? Math.floor(width / 6) + 15 : 15
    return { vertical: 65, horizontal }
}

// Only include email/name if they're set — passing empty strings would overwrite
// richer values the backend Intercom sync may already have set.
const buildIdentityFields = (email, name) => ({
    ...(email ? { email } : {}),
    ...(name ? { name } : {}),
})

// Le nom que chaque surface passe au chat : prénom et nom, ou rien s'ils manquent.
export const intercomName = (user) =>
    `${user?.firstname || ""} ${user?.lastname || ""}`.trim() || undefined

const WebIntercomChat = ({
    appId,
    userId,
    userJwt,
    email,
    name,
    hideDefaultLauncher,
    onReadyChange,
    onUnreadCountChange,
}) => {
    const [ready, setReady] = useState(false)
    const identified = !!(userId && userJwt)

    useEffect(() => {
        if (Platform.OS !== "web" || !appId || !IntercomWebSdk) return

        // Les surfaces identifiées (mode parent, app tuteur) ouvrent le chat
        // depuis le HelpFAB : le lanceur d'Intercom y est masqué. Le tunnel
        // d'abonnement, anonyme, le garde là où était l'ancien HelpFAB.
        let launcher
        if (hideDefaultLauncher) {
            launcher = { hide_default_launcher: true }
        } else {
            const padding = computeWebLauncherPadding()
            launcher = {
                alignment: "right",
                vertical_padding: padding.vertical,
                horizontal_padding: padding.horizontal,
            }
        }

        const bootData = {
            app_id: appId,
            ...(identified
                ? { user_id: String(userId), intercom_user_jwt: userJwt }
                : {}),
            ...buildIdentityFields(email, name),
            language_override: "fr",
            ...launcher,
        }

        // First mount loads the widget and boots from these settings; any later
        // run (re-identify or remount) shuts the prior session down and boots
        // fresh so a switched user/JWT never reuses the previous session. The SDK
        // queues these calls until the widget script finishes loading.
        if (webIntercomLoaded) {
            IntercomWebSdk.shutdown()
            IntercomWebSdk.boot(bootData)
        } else {
            IntercomWebSdk.default(bootData)
            webIntercomLoaded = true
        }
        setReady(true)
        onReadyChange?.(true)

        // Re-registered on every boot (a re-identify shuts the widget down,
        // dropping the prior callback), so the badge tracks the current user.
        IntercomWebSdk.onUnreadCountChange?.((count) =>
            onUnreadCountChange?.(count)
        )

        return () => {
            IntercomWebSdk.shutdown()
            setReady(false)
            onReadyChange?.(false)
            onUnreadCountChange?.(0)
        }
    }, [appId, userId, userJwt, hideDefaultLauncher])

    useEffect(() => {
        if (Platform.OS !== "web" || !ready || !IntercomWebSdk) return
        IntercomWebSdk.update({
            ...buildIdentityFields(email, name),
            language_override: "fr",
        })
    }, [email, name, ready])

    // Recale le lanceur visible quand la fenêtre change de taille — sans objet
    // une fois masqué (surfaces identifiées, où le HelpFAB tient sa place).
    useEffect(() => {
        if (
            Platform.OS !== "web" ||
            !ready ||
            !IntercomWebSdk ||
            hideDefaultLauncher
        )
            return
        const sync = () => {
            const p = computeWebLauncherPadding()
            IntercomWebSdk.update({
                vertical_padding: p.vertical,
                horizontal_padding: p.horizontal,
            })
        }
        window.addEventListener("resize", sync)
        return () => window.removeEventListener("resize", sync)
    }, [ready, hideDefaultLauncher])

    return null
}

const NativeIntercomChat = ({
    appId,
    apiKey,
    userId,
    userJwt,
    email,
    name,
    onReadyChange,
    onUnreadCountChange,
}) => {
    const [ready, setReady] = useState(false)
    const identified = !!(userId && userJwt)

    // Cleanup ALWAYS calls Intercom.logout() so the native SDK never carries a
    // previous user's session across family switches or app logout.
    useEffect(() => {
        if (!Intercom) return

        let cancelled = false
        let sessionActive = false

        const run = async () => {
            try {
                const initialized = await initializeIntercomNative(
                    appId,
                    apiKey
                )
                if (cancelled || !initialized) return
                const updateAttrs = {
                    ...buildIdentityFields(email, name),
                    languageOverride: "fr",
                }
                if (identified) {
                    // Native SDK persists the prior login; re-login is a no-op
                    // there, so a refreshed JWT never reaches the session and
                    // present() throws intercom_user_jwt_expired. Logout forces a
                    // clean re-auth with the current JWT (set before login so the
                    // Messenger-Security login call carries it).
                    await Intercom.logout().catch(() => {})
                    if (cancelled) return
                    await Intercom.setUserJwt(userJwt)
                    if (cancelled) return
                    await Intercom.loginUserWithUserAttributes({
                        userId: String(userId),
                    })
                    sessionActive = true
                    if (cancelled) return
                    await Intercom.updateUser(updateAttrs)
                } else {
                    await Intercom.loginUnidentifiedUser()
                    sessionActive = true
                    if (cancelled) return
                    await Intercom.updateUser(updateAttrs)
                }
                if (!cancelled) {
                    setReady(true)
                    onReadyChange?.(true)
                }
            } catch (e) {
                console.warn("[Intercom] Failed to start session:", e)
            }
        }

        run()

        return () => {
            cancelled = true
            onReadyChange?.(false)
            if (sessionActive) {
                Intercom.logout().catch(() => {})
            }
        }
    }, [userId, userJwt])

    useEffect(() => {
        if (!Intercom || !ready) return
        Intercom.updateUser({
            ...buildIdentityFields(email, name),
            languageOverride: "fr",
        }).catch(() => {})
    }, [email, name, ready])

    // Mirror the SDK's unread conversation count into Redux for the badge. The
    // native emitter is global (survives re-login), so one subscription once the
    // session is live is enough; bootstrap is the Android-only listener setup.
    useEffect(() => {
        if (
            !Intercom ||
            !ready ||
            !IntercomEvents ||
            !NativeModules?.IntercomEventEmitter
        )
            return

        const cleanupBootstrap = Intercom.bootstrapEventListeners?.()
        const emitter = new NativeEventEmitter(
            NativeModules.IntercomEventEmitter
        )
        const sub = emitter.addListener(
            IntercomEvents.IntercomUnreadCountDidChange,
            (response) => onUnreadCountChange?.(response?.count ?? 0)
        )
        Intercom.getUnreadConversationCount?.()
            .then((count) => onUnreadCountChange?.(count ?? 0))
            .catch(() => {})

        return () => {
            sub.remove()
            cleanupBootstrap?.()
            onUnreadCountChange?.(0)
        }
    }, [ready])

    return null
}

const IntercomChatComponent = (props) => {
    const {
        intercomAppId,
        intercomAndroidApiKey,
        intercomIosApiKey,
        intercomUserJwt,
        intercomJwtUserId,
        intercomJwtFetchedAt,
        userId,
        email,
        name,
        isParentMode,
        identified: identifiedByLayout = false,
        alwaysShow = false,
        tokenLoading,
    } = props

    // Usable only if the JWT was minted for the current user and is still fresh.
    // Guards the account-switch race (wrong user) and Intercom's
    // intercom_user_jwt_expired rejection (lapsed token) — both force a refetch.
    const usableJwt =
        !!intercomUserJwt &&
        intercomJwtUserId === userId &&
        isIntercomJwtFresh(intercomJwtFetchedAt)

    // L'identité est vérifiée sur les surfaces où l'utilisateur est connecté :
    // le mode parent, et l'app tuteur, dont le layout passe `identified`. Le
    // tunnel d'abonnement tourne en anonyme — personne n'y est encore connecté,
    // le serveur ne peut rien signer.
    const identified = isParentMode || identifiedByLayout

    // Bounded retry: cap consecutive failures at 3 (linear backoff) per
    // (user, identified) tuple. The counter resets on user/identified change
    // or any fetchedAt change (a fresh token landed or the identity was reset)
    // — never on a failing refresh that leaves a stale token in place, so a
    // down backend can't reset the streak and spin a 0ms loop.
    const tokenAttemptsRef = useRef({
        userId: null,
        identified: false,
        count: 0,
        fetchedAt: null,
    })
    useEffect(() => {
        const attempts = tokenAttemptsRef.current
        if (
            attempts.userId !== userId ||
            attempts.identified !== identified
        ) {
            tokenAttemptsRef.current = {
                userId,
                identified,
                count: 0,
                fetchedAt: intercomJwtFetchedAt,
            }
        } else if (intercomJwtFetchedAt !== attempts.fetchedAt) {
            attempts.count = 0
            attempts.fetchedAt = intercomJwtFetchedAt
        }
        if (
            !identified ||
            !userId ||
            usableJwt ||
            tokenLoading ||
            tokenAttemptsRef.current.count >= 3
        ) {
            return
        }
        const backoffMs = tokenAttemptsRef.current.count * 1000
        const timer = setTimeout(() => {
            tokenAttemptsRef.current.count++
            props.apiRequest(familyService.getIntercomIdentityToken)
        }, backoffMs)
        return () => clearTimeout(timer)
    }, [identified, userId, usableJwt, tokenLoading, intercomJwtFetchedAt])

    // Refresh a stale identity when the app/tab returns to the foreground so a
    // session left idle past the token window never opens on an expired token
    // (mobile resume + web tab refocus; the on-open hook covers active taps).
    const refreshStateRef = useRef({})
    refreshStateRef.current = { intercomUserJwt, intercomJwtFetchedAt }
    useEffect(() => {
        if (!identified) return
        const refreshIfStale = () => {
            const { intercomUserJwt: jwt, intercomJwtFetchedAt: at } =
                refreshStateRef.current
            if (jwt && !isIntercomJwtFresh(at)) {
                props.resetIntercomIdentification()
            }
        }
        if (Platform.OS === "web") {
            if (typeof window === "undefined") return
            const onVisible = () => {
                if (!document.hidden) refreshIfStale()
            }
            window.addEventListener("focus", refreshIfStale)
            document.addEventListener("visibilitychange", onVisible)
            return () => {
                window.removeEventListener("focus", refreshIfStale)
                document.removeEventListener("visibilitychange", onVisible)
            }
        }
        const sub = AppState.addEventListener("change", (next) => {
            if (next === "active") refreshIfStale()
        })
        return () => sub.remove()
    }, [identified])

    // A tapped support-reply push sets pendingIntercomOpen; open the messenger
    // the moment the session is live (after identification) and clear the flag.
    // The flag survives the mode flip + token fetch, so a cold tap that first
    // routes into parent mode still lands on the conversation once ready.
    useEffect(() => {
        if (props.pendingIntercomOpen && props.intercomReady) {
            openIntercomMessenger(props.pendingIntercomConversationId)
            props.setPendingIntercomOpen(false)
        }
    }, [props.pendingIntercomOpen, props.intercomReady])

    const session = intercomSession({ identified, alwaysShow, usableJwt })
    if (!intercomAppId || !session) return null
    const isIdentifiedSession = session === INTERCOM_SESSION.IDENTIFIED

    const handleReadyChange = (ready) => {
        props.setIntercomReady(ready)
    }

    // Log in with the "masteur_<id>" external_id the backend signs into the JWT
    // (and the CRM sync writes) — must match the JWT claim or Intercom rejects
    // the session, and aligns the in-app contact with the user's real one.
    const intercomUserId =
        isIdentifiedSession && userId ? `masteur_${userId}` : null

    if (Platform.OS === "web") {
        return (
            <WebIntercomChat
                appId={intercomAppId}
                userId={intercomUserId}
                userJwt={isIdentifiedSession ? intercomUserJwt : null}
                email={email}
                name={name}
                hideDefaultLauncher={isIdentifiedSession}
                onReadyChange={handleReadyChange}
                onUnreadCountChange={props.setIntercomUnreadCount}
            />
        )
    }

    return (
        <NativeIntercomChat
            appId={intercomAppId}
            apiKey={
                Platform.OS === "ios"
                    ? intercomIosApiKey
                    : intercomAndroidApiKey
            }
            userId={intercomUserId}
            userJwt={isIdentifiedSession ? intercomUserJwt : null}
            email={email}
            name={name}
            onReadyChange={handleReadyChange}
            onUnreadCountChange={props.setIntercomUnreadCount}
        />
    )
}

const mapStateToProps = (state) => ({
    intercomAppId: state.data.staticData?.intercom_app_id,
    intercomAndroidApiKey: state.data.staticData?.intercom_android_api_key,
    intercomIosApiKey: state.data.staticData?.intercom_ios_api_key,
    intercomUserJwt: state.intercom?.userJwt,
    intercomJwtUserId: state.intercom?.jwtUserId,
    intercomJwtFetchedAt: state.intercom?.jwtFetchedAt,
    isParentMode: state.data.family?.isParentMode || false,
    intercomReady: state.intercom?.ready || false,
    pendingIntercomOpen: state.intercom?.pendingOpen || false,
    pendingIntercomConversationId:
        state.intercom?.pendingConversationId || null,
    tokenLoading: !!state.api.getIntercomIdentityToken?.request,
})

const mapDispatchToProps = {
    apiRequest,
    setIntercomReady,
    resetIntercomIdentification,
    setPendingIntercomOpen,
    setIntercomUnreadCount,
}

const IntercomChat = connect(
    mapStateToProps,
    mapDispatchToProps
)(IntercomChatComponent)

export default IntercomChat

// Opens the messenger. With a conversationId (a tapped reply push) it lands on
// that thread; with a message, on a new conversation already written (the family
// only says why); otherwise it opens the inbox/home (the FAB and contextual CTAs).
export const openIntercomMessenger = (conversationId = null, message = null) => {
    if (Platform.OS === "web") {
        if (message && IntercomWebSdk?.showNewMessage) {
            IntercomWebSdk.showNewMessage(message)
        } else if (conversationId && IntercomWebSdk?.showConversation) {
            IntercomWebSdk.showConversation(String(conversationId))
        } else {
            IntercomWebSdk?.show()
        }
    } else if (Intercom) {
        const present = message
            ? Intercom.presentMessageComposer(message)
            : conversationId && IntercomContent
                ? Intercom.presentContent(
                      IntercomContent.conversationWithConversationId(
                          String(conversationId)
                      )
                  )
                : Intercom.present()
        present.catch((e) => console.warn("[Intercom] Failed to present:", e))
    }
}

// Shared opener for the support chat used by every entry point (the HelpFAB,
// the contextual "Changer d'offre" surface, …). If the session is fresh and
// live it opens instantly; otherwise it refreshes the token (re-fetch + native
// re-login) and opens the moment the session is live — so the user always gets
// the chat, never Intercom's expired-token screen. The 8s failsafe surfaces a
// retry hint if the refresh can't complete.
export const useOpenIntercomChat = ({ requireFreshJwt = true } = {}) => {
    const dispatch = useDispatch()
    const intercomReady = useSelector((state) => state.intercom?.ready || false)
    const fetchedAt = useSelector(
        (state) => state.intercom?.jwtFetchedAt || null
    )
    // Anonymous surfaces (subscription flow) have no JWT, so readiness alone
    // gates the open; parent surfaces also require a fresh, non-expired token.
    const canOpen = requireFreshJwt
        ? intercomReady && isIntercomJwtFresh(fetchedAt)
        : intercomReady
    const pendingRef = useRef(false)
    const pendingMessageRef = useRef(null)
    const failsafeRef = useRef(null)

    useEffect(() => {
        if (pendingRef.current && canOpen) {
            pendingRef.current = false
            if (failsafeRef.current) clearTimeout(failsafeRef.current)
            openIntercomMessenger(null, pendingMessageRef.current)
        }
    }, [canOpen])

    useEffect(
        () => () => {
            if (failsafeRef.current) clearTimeout(failsafeRef.current)
        },
        []
    )

    // A prepared message is a string; a press handler hands the opener its event, which is not one.
    return useCallback((message) => {
        const prepared =
            typeof message === "string" && message !== "" ? message : null
        if (canOpen) {
            openIntercomMessenger(null, prepared)
            return
        }
        pendingRef.current = true
        pendingMessageRef.current = prepared
        if (requireFreshJwt && !isIntercomJwtFresh(fetchedAt)) {
            dispatch(resetIntercomIdentification())
        }
        SnackBarM.show({
            text1: "Le chat se prépare, il s'ouvrira dans un instant.",
            duration: 2500,
        })
        if (failsafeRef.current) clearTimeout(failsafeRef.current)
        failsafeRef.current = setTimeout(() => {
            if (!pendingRef.current) return
            pendingRef.current = false
            SnackBarM.show({
                text1: "Le chat est momentanément indisponible. Réessaie dans un instant.",
                duration: 3000,
            })
        }, 8000)
    }, [canOpen, requireFreshJwt, fetchedAt, dispatch])
}
